import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BottomNavBar from "@/components/BottomNavBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Gift, Heart, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const PRESET_AMOUNTS = [2500, 5000, 10000, 15000, 25000, 50000];

export default function GiftCardPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [form, setForm] = useState({
        amount_jmd: 5000,
        recipient_name: "",
        recipient_email: "",
        recipient_phone: "",
        message: "",
        sender_name: user?.name || "",
    });
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!user) {
            toast.message("Sign in to send a gift card");
            navigate("/auth?next=/giftcards");
            return;
        }
        if (!form.recipient_name || !form.recipient_email) return toast.error("Recipient name and email are required");
        setSubmitting(true);
        try {
            const { data } = await api.post("/giftcards/purchase", {
                ...form,
                amount_jmd: parseFloat(form.amount_jmd),
            });
            if (data.checkout_url) {
                window.location.href = data.checkout_url;
            } else {
                toast.success("Gift card created.");
                navigate("/giftcards/mine");
            }
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-background pb-24" data-testid="giftcard-page">
            <Header />
            <section className="max-w-4xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
                <div className="text-center mb-10">
                    <Gift size={44} className="gold-text mx-auto mb-6" strokeWidth={1} />
                    <div className="overline mb-3">Royal gift cards</div>
                    <h1 className="font-serif text-5xl sm:text-6xl tracking-tighter">
                        Send help <span className="italic gold-grad">back home.</span>
                    </h1>
                    <p className="mt-5 text-muted-foreground max-w-xl mx-auto leading-relaxed">
                        For the Jamaican you love abroad or at home. One tap and your mother gets a clean,
                        your sister gets groceries, your father gets a driver. No excuses.
                    </p>
                </div>

                <form onSubmit={submit} className="bg-card border border-white/10 rounded-sm p-6 lg:p-10 space-y-6" data-testid="giftcard-form">
                    <div>
                        <div className="overline mb-3">Choose an amount (JMD)</div>
                        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                            {PRESET_AMOUNTS.map((a) => (
                                <button
                                    key={a}
                                    type="button"
                                    onClick={() => setForm({ ...form, amount_jmd: a })}
                                    className={`py-3 rounded-sm border text-sm font-medium transition-colors ${form.amount_jmd === a ? "border-primary bg-primary/15 gold-text" : "border-white/10 text-muted-foreground hover:border-white/30"}`}
                                    data-testid={`giftcard-preset-${a}`}
                                >
                                    ${a.toLocaleString()}
                                </button>
                            ))}
                        </div>
                        <Input
                            type="number"
                            placeholder="Or custom amount"
                            min="1000"
                            max="200000"
                            value={form.amount_jmd}
                            onChange={(e) => setForm({ ...form, amount_jmd: e.target.value })}
                            className="mt-3 bg-input border-white/15 h-11 rounded-sm"
                            data-testid="giftcard-amount-input"
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <div className="overline mb-2">To (name)</div>
                            <Input value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} placeholder="Mom / Sister / Mr. Brown" className="bg-input border-white/15 h-11 rounded-sm" data-testid="giftcard-recipient-name-input" />
                        </div>
                        <div>
                            <div className="overline mb-2">To (email)</div>
                            <Input type="email" value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} placeholder="mom@email.com" className="bg-input border-white/15 h-11 rounded-sm" data-testid="giftcard-recipient-email-input" />
                        </div>
                    </div>

                    <div>
                        <div className="overline mb-2">Phone (optional, for WhatsApp copy)</div>
                        <Input value={form.recipient_phone} onChange={(e) => setForm({ ...form, recipient_phone: e.target.value })} placeholder="+1 876 ..." className="bg-input border-white/15 h-11 rounded-sm" data-testid="giftcard-recipient-phone-input" />
                    </div>

                    <div>
                        <div className="overline mb-2">Your message</div>
                        <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} placeholder="Thinking of you — stay royal 👑" className="bg-input border-white/15 rounded-sm" data-testid="giftcard-message-input" />
                    </div>

                    <div>
                        <div className="overline mb-2">From</div>
                        <Input value={form.sender_name} onChange={(e) => setForm({ ...form, sender_name: e.target.value })} placeholder="Your name" className="bg-input border-white/15 h-11 rounded-sm" data-testid="giftcard-sender-name-input" />
                    </div>

                    <div className="pt-4 border-t border-white/10 flex items-center justify-between flex-wrap gap-4">
                        <div className="text-xs text-muted-foreground max-w-sm">
                            Delivered instantly by email once payment settles. Redeemable on every SMART service.
                        </div>
                        <Button type="submit" disabled={submitting} className="bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-12 px-6" data-testid="giftcard-submit-btn">
                            <Heart size={14} className="mr-2" />
                            {submitting ? "Opening checkout…" : <>Send gift <ArrowRight size={14} className="ml-2" /></>}
                        </Button>
                    </div>
                </form>
            </section>
            <Footer />
            <BottomNavBar />
        </div>
    );
}
